import {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import nth from 'lodash/nth';

import {useAppDispatch, useAppSelector, useBreakPoint, useDestructiveDialog, useFeatureToggle, useModal} from '@hooks';

import {
  type MeetingRoom,
  getBuildingByCheckInOrWorkspaceReservation,
  getFloorsWithMapByBuildingId,
  getUser,
  loadAllMeetingRooms,
  withAsyncThunkErrorHandling,
  getFloorById,
  getShouldShowHereAndNowConnectYourCalendarFullScreen,
  getDisplayCalendarSyncTile,
  type Area,
  getShouldShowHereAndNow,
  getIsLoadingCalendarState,
  getAllMeetingRoomLoadingStatus,
  getIsLoadingUserCheckIn,
  getIsLoadingWorkspaceReservations,
  getIsOccupancyEnabled,
  loadWorkspaceOccupancy,
  getWorkspaceOccupancyLoadingStatusForBuildingId,
  getAllMeetingRoomSuggestions,
  getIsOccupancyBasedHereAndNow,
  getAppLanguage,
  getFloorsByBuildingId,
  getAvailableDeskAmenityDataByBuildingId,
  getAvailableActivityTypeDataByBuildingId,
  getAvailableDeskAmenitiesForBuilding,
  getAvailableActivityTypesForBuilding,
  getAreasWithSensorData,
  AreaWithSensorInfo,
  getDefaultBuilding,
  getAllBuildings,
  getBuildingById,
  trackEvent,
} from '@lib/store';

import {addMinutes, parseISO, startOfDay, startOfMinute, differenceInMinutes} from 'date-fns';

import {DEFAULT_NEW_EVENT_DURATION as DURATION, MINUTES_TRESHOLD_FOR_STALE_DATA} from '@constants';
import {Div, FlexCol, H3} from '@quarks';
import {
  HaNControls,
  HaNMaps,
  HaNModalContainer,
  HaNFilters,
  HaNHorizontalListContent,
  HaNVerticalListContent,
  isMeetingRoom,
  HaNCalendarSyncTile,
  HaNCalendarSync,
  SingleSelectCard,
} from '@organisms';
import {Canvas, Heading, HereAndNowLayout} from '@templates';

import {AreaEventTarget, RoomEventTarget} from '../../../../../../submodules/map/mapiq-map/EventTarget';
import {ControlsRow, HaNPageHeader, MapWrapper, RoomListGridArea, StyledNavLink, StyledTabs, Title} from './styles';
import {IconButton, SwiperRefType} from '@molecules';
import {Navigate, useParams} from 'react-router-dom';
import {Loader} from '@atoms';
import {constructHereAndNowRoomsFilter, constructHereAndNowWorkspacesFilter, useFilterData} from '@lib/utils';
import {getDeskAmenityIcon} from '@utils';

const initialStartDateTime = startOfMinute(new Date()).toISOString();
const initialEndDateTime = addMinutes(parseISO(initialStartDateTime), 30).toISOString();

export const HereAndNow = () => {
  // * UI
  const ref = useRef<SwiperRefType | null>(null);
  const {t} = useTranslation();
  const breakPoint = useBreakPoint();
  const {tab} = useParams();
  const {addPages, openModal} = useModal();
  const {destructiveDialog} = useDestructiveDialog();

  // * FILTERS
  const [{startDateTime, endDateTime}, setRoomDateTimes] = useState({
    startDateTime: initialStartDateTime,
    endDateTime: initialEndDateTime,
  });
  const occupancyLoadedDateTimeRef = useRef<Date | undefined>();

  // * STATE
  const [roomInformationOpen, setRoomInformationOpen] = useState(false);
  const [warnBeforeOpeningNewRoom, setWarnBeforeOpeningNewRoom] = useState(false);
  const [hoverRoomId, setHoverRoomId] = useState<string | null>(null);
  const [selectedObject, setSelectedObject] = useState<MeetingRoom | AreaWithSensorInfo | null>(null);
  const [selectedObjectForMap, setSelectedObjectForMap] = useState<MeetingRoom | AreaWithSensorInfo | null>(null);

  // * REDUX SELECTORS
  const dispatch = useAppDispatch();
  const building = useAppSelector((state) =>
    getBuildingByCheckInOrWorkspaceReservation(state, startOfDay(new Date()).toISOString()),
  );
  const sessionBuildingId = sessionStorage.getItem('buildingId');

  const {id: reservationBuildingId = ''} = building ?? {};
  const [buildingId, setBuildingId] = useState(sessionBuildingId || reservationBuildingId);

  const isHereAndNowEnabled = useAppSelector(getShouldShowHereAndNow);
  const minutesThresholdForStaleData = 5;
  const showCalendarConsentMessageNoOccupancy = useAppSelector(getShouldShowHereAndNowConnectYourCalendarFullScreen);
  const displayCalendarSyncTile = useAppSelector(getDisplayCalendarSyncTile);
  const {WorkspacesOnHereAndNow: isWorkspacesOnHereAndNowEnabled} = useFeatureToggle();
  const calendarLoadingStatus = useAppSelector(getIsLoadingCalendarState);
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const isOccupancyBased = useAppSelector(getIsOccupancyBasedHereAndNow);
  const floors = useAppSelector((state) => getFloorsByBuildingId(state, buildingId));
  const appLanguage = useAppSelector(getAppLanguage);
  const loadingCalendar = calendarLoadingStatus === 'Loading' || calendarLoadingStatus === 'NotLoaded';
  const occupancyLoadingStatus = useAppSelector((state) =>
    getWorkspaceOccupancyLoadingStatusForBuildingId(state, buildingId),
  );
  const isLoadingSensors = occupancyLoadingStatus === 'loading' && isOccupancyEnabled;
  const meetingRoomLoadingStatus = useAppSelector(getAllMeetingRoomLoadingStatus);
  const isLoadingUserCheckIn = useAppSelector(getIsLoadingUserCheckIn);
  const isLoadingWorkspaceReservations = useAppSelector(getIsLoadingWorkspaceReservations);
  const isLoadingBuilding = isLoadingUserCheckIn || isLoadingWorkspaceReservations;
  const loadingMeetingRooms = meetingRoomLoadingStatus === 'Loading' || meetingRoomLoadingStatus === 'NotLoaded';
  const isLoading = loadingCalendar || loadingMeetingRooms || isLoadingSensors;
  const areasWithSensorData = useAppSelector((state) => getAreasWithSensorData(state, buildingId, t));
  const allRooms = useAppSelector(getAllMeetingRoomSuggestions);
  const {availableDeskAmenities, status: deskAmenitiesLoadingStatus} = useAppSelector(
    getAvailableDeskAmenityDataByBuildingId(buildingId),
  );
  const {availableActivityTypes, status: activityTypesLoadingStatus} = useAppSelector(
    getAvailableActivityTypeDataByBuildingId(buildingId),
  );
  const {id: loggedInUserId} = useAppSelector(getUser);
  const defaultBuilding = useAppSelector(getDefaultBuilding);
  const allBuildings = useAppSelector(getAllBuildings);

  const buildingName = useAppSelector((state) => getBuildingById(state, buildingId))?.name || t('translation:Booked');

  // FILTERS

  const roomFilters = useMemo(
    () => constructHereAndNowRoomsFilter(floors, !isOccupancyBased, t, appLanguage),
    [appLanguage, floors, isOccupancyBased, t],
  );

  const areaFilters = useMemo(
    () =>
      constructHereAndNowWorkspacesFilter(
        floors,
        availableDeskAmenities,
        availableActivityTypes,
        getDeskAmenityIcon,
        isOccupancyEnabled,
        t,
      ),
    [availableActivityTypes, availableDeskAmenities, floors, isOccupancyEnabled, t],
  );

  const {filteredData: rooms, selectedFilters: roomSelectedFilters} = useFilterData(
    'HereAndNow_Rooms',
    buildingId,
    allRooms,
    roomFilters,
  );

  const {filteredData: areas} = useFilterData('HereAndNow_Workspaces', buildingId, areasWithSensorData, areaFilters);

  const mapFloorId = selectedObjectForMap?.floorId
    ? selectedObjectForMap.floorId
    : tab === 'rooms'
    ? rooms[0]?.floorId
    : areas[0]?.floorId || null;

  const floorsWithMap = useAppSelector((state) => getFloorsWithMapByBuildingId(state, buildingId));
  const mapFloor = useAppSelector((state) => getFloorById(state, mapFloorId || ''));
  const [roomListVerticalOpen, setRoomListVerticalOpen] = useState(floorsWithMap.length ? false : true);

  // * CONSTS

  const duration = Number(roomSelectedFilters['SECTION_ID_DURATION']?.[0]) || DURATION;
  const displayControlsRow =
    !loadingMeetingRooms && floorsWithMap.length && rooms.length && mapFloor && !showCalendarConsentMessageNoOccupancy;

  const paddingLeftPixels = breakPoint === 'large' ? 420 : 0; // Width of a modal
  const paddingBottomPixels = breakPoint === 'small' ? 100 : 0; // Height of a horizontal rooms list
  const areFiltersTransparent = breakPoint === 'small' && roomListVerticalOpen;

  const viewportRestrictions = useMemo(
    () => ({
      paddingTopPixels: 80, // Filters height and padding
      paddingRightPixels: 0,
      paddingBottomPixels: paddingBottomPixels,
      paddingLeftPixels,
    }),
    [paddingLeftPixels, paddingBottomPixels],
  );

  const objectToSelectInitially = tab === 'workspaces' ? areas[0] ?? null : rooms[0] ?? null;
  const isInMobielView = breakPoint === 'small';
  const hasInitallySelectedObject = selectedObject !== null;

  // * FUNCS

  const loadHereAndNowRooms = useCallback(() => {
    const loadRooms = async () => {
      await dispatch(
        withAsyncThunkErrorHandling(() =>
          loadAllMeetingRooms({
            startDateTime: newStartDateTime,
            endDateTime: newEndDateTime,
            buildingId: buildingId,
            internalAttendeeIds: [loggedInUserId],
            totalAttendees: 1,
          }),
        ),
      );
    };

    const newStartDateTime = startOfMinute(new Date()).toISOString();
    const newEndDateTime = addMinutes(parseISO(newStartDateTime), duration).toISOString();

    setRoomDateTimes({startDateTime: newStartDateTime, endDateTime: newEndDateTime});

    if (buildingId && duration && loggedInUserId) {
      loadRooms();
    }
  }, [buildingId, dispatch, duration, loggedInUserId]);

  const fetchOccupancySensorData = useCallback(() => {
    if (!isLoadingBuilding && buildingId) {
      dispatch(withAsyncThunkErrorHandling(() => loadWorkspaceOccupancy(buildingId)));

      occupancyLoadedDateTimeRef.current = startOfMinute(new Date());
    }
  }, [buildingId, dispatch, isLoadingBuilding]);

  const refreshRoomsData = () => {
    if (buildingId && loggedInUserId) {
      const now = startOfMinute(new Date());
      if (differenceInMinutes(now, parseISO(startDateTime)) >= MINUTES_TRESHOLD_FOR_STALE_DATA) {
        loadHereAndNowRooms();
      }
    }
  };

  const toggleListOrMapView = () => {
    if (!roomListVerticalOpen) {
      ref.current?.scrollToSlide(0);
    }
    setRoomListVerticalOpen(!roomListVerticalOpen);
  };

  const openObjectInformation = async (entity: MeetingRoom | AreaWithSensorInfo | undefined) => {
    // Some of the areas have capacity of 0 which do not get fetched
    // because of that they will not be found and we need not handle possible undefined value
    if (!entity) {
      setSelectedObjectForMap(null);
      setSelectedObject(null);
      return;
    } else if (isMeetingRoom(entity)) {
      if (warnBeforeOpeningNewRoom) {
        const confirmation = await destructiveDialog(
          t('meeting:DiscardEventChangesTitle'),
          t('meeting:DiscardEventChangesMessage'),
          'warning',
          t('meeting:DiscardEventChangesConfirm'),
        );
        if (confirmation) {
          setSelectedObjectForMap(entity);
          setSelectedObject(entity);
          setRoomInformationOpen(true);
        }
      } else {
        setSelectedObjectForMap(entity);
        setSelectedObject(entity);
        setRoomInformationOpen(true);
      }
    } else {
      setSelectedObject(entity);
      setSelectedObjectForMap(entity);
      setRoomInformationOpen(true);
    }
  };

  const closeDetailsModal = () => {
    setRoomInformationOpen(false);
    setSelectedObject(null);
  };

  const resetListState = () => {
    if (breakPoint === 'small') {
      ref.current?.scrollToSlide(0);
    }
  };

  const resetEntityState = useCallback(
    (tabValue?: string) => {
      if (breakPoint === 'small') {
        const objectToSelect = tabValue === 'rooms' ? rooms[0] : areas[0];
        setRoomInformationOpen(false);
        setSelectedObject(objectToSelect);
        setSelectedObjectForMap(objectToSelect);
      } else {
        setRoomInformationOpen(false);
        setSelectedObject(null);
        setSelectedObjectForMap(null);
      }
    },
    [areas, breakPoint, rooms],
  );

  const handleRoomClick = (e: RoomEventTarget | AreaEventTarget) => {
    if (breakPoint === 'small') {
      if (e.type === 'room') {
        ref?.current?.scrollToSlide(rooms.findIndex((room) => room.id === e.roomId));
      } else {
        ref?.current?.scrollToSlide(areas.findIndex((area) => area.id === e.areaId));
      }
    } else {
      if (e.roomId) {
        const clickedRoom = rooms.find((room) => room.id === e.roomId);
        openObjectInformation(clickedRoom);
      } else if (e.areaId) {
        const clickedArea = areas.find((area) => area.id === e.areaId);
        openObjectInformation(clickedArea);
      }
    }
  };

  const handleOnSlideChange = (visibleObject: MeetingRoom | AreaWithSensorInfo) => {
    setSelectedObject(visibleObject);
    setSelectedObjectForMap(visibleObject);
  };

  const handleBookNow = () => {
    if (selectedObject !== null) {
      if (isMeetingRoom(selectedObject) && breakPoint === 'small') {
        const currentIndex = rooms.indexOf(selectedObject!);
        const nextRoomValue = nth(rooms, currentIndex + 1) ?? nth(rooms, currentIndex - 1) ?? null;
        setSelectedObjectForMap(nextRoomValue);
        setSelectedObject(nextRoomValue);
      }
    }
  };

  const handleBuildingSelectClick = () => {
    addPages([
      <SingleSelectCard
        defaultId={defaultBuilding?.id || buildingId}
        preselectedId={buildingId}
        headerTitle={t('screen:MeetingBuildingSelection')}
        entities={allBuildings.reduce(
          (buildings, building) => {
            if (building.id !== defaultBuilding?.id) {
              buildings.push({
                id: building?.id ?? '',
                label: building?.name ?? '',
              });
            }
            return buildings;
          },
          [{id: defaultBuilding?.id || '', label: defaultBuilding?.name || ''}],
        )}
        onSelect={(buildingId: string) => {
          dispatch(trackEvent('HereAndNow_ChangeBuilding', {id: buildingId}));
          setBuildingId(buildingId);
        }}
      />,
    ]);
    openModal();
  };

  // * SIDEEFFECTS

  useEffect(() => {
    // For the building selector we want to preserve the selection
    // This particular implementation with session storage is a one off solution.

    sessionStorage.setItem('buildingId', buildingId);
  }, [buildingId]);

  useEffect(() => {
    if (isLoadingBuilding || !buildingId) return;

    if (deskAmenitiesLoadingStatus === 'NotLoaded') {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableDeskAmenitiesForBuilding({buildingId})));
    }

    if (activityTypesLoadingStatus === 'NotLoaded') {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableActivityTypesForBuilding({buildingId})));
    }
  }, [activityTypesLoadingStatus, buildingId, deskAmenitiesLoadingStatus, dispatch, isLoadingBuilding]);

  useEffect(() => {
    const correctTab = tab === 'workspaces' || tab === 'rooms';
    function matchObjecAndTabType(objectType: MeetingRoom['type'] | Area['type'], tabType: 'workspaces' | 'rooms') {
      if (
        (objectType === 'area' && tabType === 'workspaces') ||
        (objectType === 'meetingRoom' && tabType === 'rooms')
      ) {
        return true;
      }
      return false;
    }
    if (correctTab && selectedObject && !matchObjecAndTabType(selectedObject.type, tab)) resetEntityState(tab);
  }, [resetEntityState, selectedObject, tab]);

  useEffect(() => {
    // We are currently fetching sensor data even when on rooms tab,
    // looking to optimise this in the future with reduxQuery.

    if (!isOccupancyEnabled || !isWorkspacesOnHereAndNowEnabled) return;

    const now = startOfMinute(new Date());

    if (
      !occupancyLoadedDateTimeRef.current ||
      differenceInMinutes(now, occupancyLoadedDateTimeRef.current) >= minutesThresholdForStaleData
    ) {
      fetchOccupancySensorData();
    }
  }, [isOccupancyEnabled, fetchOccupancySensorData, isWorkspacesOnHereAndNowEnabled]);

  useEffect(() => {
    setRoomListVerticalOpen(floorsWithMap.length ? false : true);
  }, [floorsWithMap]);

  useEffect(() => loadHereAndNowRooms(), [loadHereAndNowRooms]);

  useEffect(() => {
    if (isInMobielView && !hasInitallySelectedObject) {
      // On mobile we need to pre-select a room, but not on larger screens
      setSelectedObject(objectToSelectInitially);
      setSelectedObjectForMap(objectToSelectInitially);
    }
  }, [hasInitallySelectedObject, isInMobielView, objectToSelectInitially]);

  // * UI

  if (isLoading) {
    return (
      <Canvas centerContent>
        <Loader />
      </Canvas>
    );
  }

  if (!isHereAndNowEnabled) {
    return <Navigate to="/fourohfour" />;
  }

  if (!tab && isWorkspacesOnHereAndNowEnabled) {
    return <Navigate to="/now/rooms" />;
  }

  if (tab !== 'rooms' && tab !== 'workspaces' && tab) {
    return <Navigate to="/fourohfour" />;
  }

  if (!isWorkspacesOnHereAndNowEnabled && tab) {
    return <Navigate to="/fourohfour" />;
  }

  return (
    <>
      <Heading>
        <HaNPageHeader
          {...(floorsWithMap.length && {
            buttonIcons: [
              {
                icon: 'office',
                label: t('Building'),
                showButton: 'mobile',
                onClick: handleBuildingSelectClick,
              },
              {
                icon: roomListVerticalOpen ? 'map' : 'weekView',
                label: roomListVerticalOpen ? t('hereAndNow:Map') : t('hereAndNow:ListOfRooms'),
                onClick: toggleListOrMapView,
                showButton: 'mobile',
              },
            ],
          })}
          title={buildingName}
        />
      </Heading>
      <HereAndNowLayout
        $displayCalendarSyncTile={displayCalendarSyncTile}
        $areWorkspacesEnabled={isWorkspacesOnHereAndNowEnabled}>
        <MapWrapper>
          <HaNMaps
            buildingId={buildingId}
            floorId={mapFloorId}
            floorsWithMap={floorsWithMap}
            hoverObjectId={hoverRoomId}
            onObjectClick={handleRoomClick}
            rooms={rooms}
            workspaces={areas}
            selectedObject={selectedObject}
            viewportRestrictions={viewportRestrictions}
          />
        </MapWrapper>
        {displayCalendarSyncTile && (
          <Div
            display="none"
            sm={{display: 'grid', gridArea: 'calendar-sync-tile', zIndex: 1}}>
            <HaNCalendarSyncTile />
          </Div>
        )}
        {isWorkspacesOnHereAndNowEnabled && (
          <Div
            gridArea="tabs"
            display="none"
            zIndex={10}
            sm={{
              display: roomListVerticalOpen ? 'none' : 'block',
            }}>
            <StyledTabs>
              <StyledNavLink
                onClick={() => {
                  resetEntityState('rooms');
                  refreshRoomsData();
                }}
                $isActive={tab === 'rooms'}
                to={'/now/rooms'}>
                {t('hereAndNow:Rooms')}
              </StyledNavLink>
              <StyledNavLink
                onClick={() => resetEntityState('workspaces')}
                $isActive={tab === 'workspaces'}
                to={'/now/workspaces'}>
                {t('hereAndNow:Workspaces')}
              </StyledNavLink>
            </StyledTabs>
          </Div>
        )}
        {!showCalendarConsentMessageNoOccupancy ? (
          <Div
            gridArea="filter"
            zIndex={1}
            overflowX={'hidden'}>
            <HaNFilters
              swiperFilterReset={resetListState}
              areFilterTransparent={areFiltersTransparent}
            />
          </Div>
        ) : null}
        {displayControlsRow ? (
          <ControlsRow
            alignItems="center"
            gridArea="controls"
            justifyContent="flex-end"
            gap={8}
            zIndex={1}>
            <HaNControls floor={mapFloor?.number} />
          </ControlsRow>
        ) : null}
        <Div
          gridArea={
            showCalendarConsentMessageNoOccupancy
              ? 'controls / room-list-horizontal / room-list-horizontal / room-list-horizontal'
              : 'room-list-horizontal'
          }
          display="none"
          sm={{display: 'grid'}}>
          <HaNHorizontalListContent
            showCalendarConsentMessageNoOccupancy={showCalendarConsentMessageNoOccupancy}
            endDateTime={endDateTime}
            onClick={openObjectInformation}
            onSlideChange={handleOnSlideChange}
            rooms={rooms}
            startDateTime={startDateTime}
            ref={ref}
            areas={areas}
          />
        </Div>
        <RoomListGridArea
          gridArea="room-list"
          overflow="hidden"
          zIndex={2}
          height="calc(var(--100vh, 100vh) - 60px)"
          $roomListVerticalOpen={roomListVerticalOpen}>
          <Div
            position="relative"
            top="0"
            zIndex={1}
            left="0">
            {displayCalendarSyncTile && (
              <Div sm={{display: 'grid', gridArea: 'calendar-sync-tile', zIndex: 1}}>
                <HaNCalendarSyncTile />
              </Div>
            )}
            <Div
              sm={{display: 'none'}}
              zIndex={10}
              gridArea="title">
              <Title>
                <H3 as="h1">{buildingName}</H3>
                <IconButton
                  aria-label="building-select"
                  icon="office"
                  iconButton="tertiary"
                  onClick={handleBuildingSelectClick}
                />
              </Title>
            </Div>
            {isWorkspacesOnHereAndNowEnabled && (
              <StyledTabs
                gridArea="tabs"
                zIndex={10}>
                <StyledNavLink
                  $isActive={tab === 'rooms'}
                  onClick={() => {
                    resetEntityState('rooms');
                    refreshRoomsData();
                  }}
                  to={'/now/rooms'}>
                  {t('hereAndNow:Rooms')}
                </StyledNavLink>
                <StyledNavLink
                  $isActive={tab === 'workspaces'}
                  onClick={() => resetEntityState('workspaces')}
                  to={'/now/workspaces'}>
                  {t('hereAndNow:Workspaces')}
                </StyledNavLink>
              </StyledTabs>
            )}
          </Div>
          <Div
            display="none"
            sm={{display: 'block', padding: '16px 20px'}}>
            <HaNFilters
              swiperFilterReset={resetListState}
              areFilterTransparent={areFiltersTransparent}
            />
          </Div>
          <FlexCol
            sm={{display: !roomListVerticalOpen ? 'none' : 'flex'}}
            overflow="hidden auto"
            flex="auto"
            zIndex={2}>
            {showCalendarConsentMessageNoOccupancy ? (
              <HaNCalendarSync />
            ) : (
              <HaNVerticalListContent
                endDateTime={endDateTime}
                onClick={openObjectInformation}
                onHover={setHoverRoomId}
                rooms={rooms}
                selectedObject={selectedObject}
                startDateTime={startDateTime}
                areas={areas}
              />
            )}
          </FlexCol>
        </RoomListGridArea>
      </HereAndNowLayout>

      <HaNModalContainer
        setWarnBeforeOpeningNewRoom={setWarnBeforeOpeningNewRoom}
        onClose={closeDetailsModal}
        entity={selectedObject}
        show={roomInformationOpen}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        duration={duration}
        onBookNowCallback={handleBookNow}
      />
    </>
  );
};
